import { isTodayBirthday } from './ageCalculator';

export interface PracticeSession {
  id: string;
  studentId: string;
  lessonId: string;
  plannedTotalMinutes: number;
  plannedDurations: Record<string, number>;
  actualDurations: Record<string, number>;
  completed: boolean;
  startAt: any;
  endAt?: any;
  durationSec?: number;
  taskCounts: Record<string, number>;
  createdAt: any;
}

export interface User {
  uid: string;
  name: string;
  nickname?: string;
  birthdate?: string;
  practiceSessionDuration?: number;
  practiceSessionsPerWeek?: number;
}

/**
 * Calculate all possible practice-earned badges for a user
 */
export const calculateAllPracticeBadges = (sessions: PracticeSession[], user?: User): string[] => {
  const badges: string[] = [];
  
  if (sessions.length === 0) return badges;
  
  // Sort sessions by date for processing
  const sortedSessions = sessions.sort((a, b) => {
    const aDate = a.endAt?.toDate?.() || new Date(a.endAt || 0);
    const bDate = b.endAt?.toDate?.() || new Date(b.endAt || 0);
    return aDate.getTime() - bDate.getTime();
  });
  
  // Calculate all metrics at once
  let cumulativeMinutes = 0;
  const weeks = new Set<string>();
  const practiceDays = new Set<string>();
  const uniquePieces = new Set<string>();
  const uniqueCategories = new Set<string>();
  let maxTechniqueStreak = 0;
  let currentTechniqueStreak = 0;
  let weekendPractices = new Set<string>();
  let earlyBirdCount = 0;
  let nightOwlCount = 0;
  let marathonCount = 0;
  let comebackKidEarned = false;
  
  // Process all sessions to calculate metrics
  for (let i = 0; i < sortedSessions.length; i++) {
    const session = sortedSessions[i];
    const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
    const sessionStartTime = session.startAt?.toDate?.() || new Date(session.startAt || 0);
    const sessionDuration = (session.durationSec || 0) / 60;
    
    cumulativeMinutes += sessionDuration;
    
    // Track practice days
    const dayKey = sessionDate.toISOString().split('T')[0];
    practiceDays.add(dayKey);
    
    // Track weekly goals
    const weekStart = new Date(sessionDate);
    weekStart.setDate(sessionDate.getDate() - sessionDate.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const weekKey = weekStart.toISOString().split('T')[0];
    weeks.add(weekKey);
    
    // Track unique pieces (from taskCounts)
    if (session.taskCounts) {
      Object.keys(session.taskCounts).forEach(taskId => {
        uniquePieces.add(taskId);
      });
    }
    
    // Track unique categories (from plannedDurations)
    if (session.plannedDurations) {
      Object.keys(session.plannedDurations).forEach(taskId => {
        uniqueCategories.add(taskId);
      });
    }
    
    // Track technique focus
    const hasTechnique = session.taskCounts && Object.keys(session.taskCounts).some(taskId => 
      taskId.toLowerCase().includes('technique')
    );
    
    if (hasTechnique) {
      currentTechniqueStreak++;
      maxTechniqueStreak = Math.max(maxTechniqueStreak, currentTechniqueStreak);
    } else {
      currentTechniqueStreak = 0;
    }
    
    // Track weekend practices
    const dayOfWeek = sessionDate.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) { // Sunday or Saturday
      const weekendKey = `${weekKey}-${dayOfWeek}`;
      weekendPractices.add(weekendKey);
    }
    
    // Track early bird and night owl
    const hour = sessionStartTime.getHours();
    if (hour < 8) earlyBirdCount++;
    if (hour >= 20) nightOwlCount++;
    
    // Track marathon sessions
    if (sessionDuration >= 120) { // 2+ hours
      marathonCount++;
    }
    
    // Check for comeback kid (return after 7+ day break)
    if (i > 0 && !comebackKidEarned) {
      const prevSession = sortedSessions[i - 1];
      const prevDate = prevSession.endAt?.toDate?.() || new Date(prevSession.endAt || 0);
      const daysBetween = Math.floor((sessionDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysBetween >= 7) {
        badges.push('comeback-kid');
        comebackKidEarned = true;
      }
    }
  }
  
  // Calculate final metrics
  const weeklyGoals = weeks.size;
  const currentStreak = calculateCurrentStreak(sortedSessions);
  const avgSessionLength = cumulativeMinutes / sortedSessions.length;
  
  // Award badges based on final metrics (no duplicates)
  if (currentStreak >= 1) badges.push('first-day');
  if (currentStreak >= 3) badges.push('three-day-streak');
  if (currentStreak >= 7) badges.push('week-streak');
  if (currentStreak >= 14) badges.push('two-week-streak');
  if (currentStreak >= 30) badges.push('month-streak');
  
  if (weeklyGoals >= 1) badges.push('first-week');
  if (weeklyGoals >= 4) badges.push('month-consistency');
  if (weeklyGoals >= 12) badges.push('quarter-master');
  
  if (cumulativeMinutes >= 60) badges.push('hour-master');
  if (cumulativeMinutes >= 300) badges.push('five-hour-club');
  if (cumulativeMinutes >= 1000) badges.push('thousand-minute-master');
  if (cumulativeMinutes >= 5000) badges.push('practice-legend');
  
  if (avgSessionLength >= 30) badges.push('focused-practicer');
  if (avgSessionLength >= 60) badges.push('dedicated-musician');
  
  if (sortedSessions.length >= 10) badges.push('quick-learner');
  if (sortedSessions.length >= 100) badges.push('century-club');
  if (sortedSessions.length >= 1000) badges.push('thousand-club');
  
  if (uniquePieces.size >= 5) badges.push('curious-mind');
  if (uniqueCategories.size >= 3) badges.push('explorer');
  
  if (maxTechniqueStreak >= 5) badges.push('technique-master');
  
  if (earlyBirdCount >= 10) badges.push('early-bird');
  if (nightOwlCount >= 10) badges.push('night-owl');
  
  if (weekendPractices.size >= 4) badges.push('weekend-warrior');
  if (marathonCount >= 3) badges.push('marathon-session');
  
  if (practiceDays.size >= 30) badges.push('first-month');
  if (practiceDays.size >= 180) badges.push('half-year-hero');
  if (practiceDays.size >= 365) badges.push('year-of-music');
  
  // Check for birthday badge - if today is their birthday and they practiced today
  if (user?.birthdate && isTodayBirthday(user.birthdate)) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if they practiced today
    const practicedToday = sessions.some(session => {
      const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate.getTime() === today.getTime();
    });
    
    if (practicedToday && !badges.includes('birthday-celebration')) {
      badges.push('birthday-celebration');
    }
  }
  
  return badges;
};

/**
 * Calculate current practice streak
 */
export const calculateCurrentStreak = (sessions: PracticeSession[]): number => {
  if (sessions.length === 0) return 0;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let streak = 0;
  let checkDate = new Date(today);
  
  // Sort sessions by date (most recent first)
  const sortedSessions = sessions.sort((a, b) => {
    const aDate = a.endAt?.toDate?.() || new Date(a.endAt || 0);
    const bDate = b.endAt?.toDate?.() || new Date(b.endAt || 0);
    return bDate.getTime() - aDate.getTime();
  });
  
  for (const session of sortedSessions) {
    const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
    sessionDate.setHours(0, 0, 0, 0);
    
    if (sessionDate.getTime() === checkDate.getTime()) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else if (sessionDate.getTime() < checkDate.getTime()) {
      break;
    }
  }
  
  return streak;
};

/**
 * Load and calculate all badges for a user (practice-earned + teacher-assigned)
 */
export const loadAllUserBadges = async (userId: string, db: any): Promise<string[]> => {
  try {
    // Load all completed practice sessions
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const sessionsQuery = query(
      collection(db, 'practiceSessions'),
      where('studentId', '==', userId),
      where('completed', '==', true)
    );
    
    const sessionsSnapshot = await getDocs(sessionsQuery);
    const sessions: PracticeSession[] = [];
    
    sessionsSnapshot.forEach((doc) => {
      const data = doc.data();
      sessions.push({
        id: doc.id,
        studentId: data.studentId,
        lessonId: data.lessonId,
        plannedTotalMinutes: data.plannedTotalMinutes,
        plannedDurations: data.plannedDurations,
        actualDurations: data.actualDurations,
        completed: data.completed,
        startAt: data.startAt,
        endAt: data.endAt,
        durationSec: data.durationSec,
        taskCounts: data.taskCounts,
        createdAt: data.createdAt
      });
    });

    // Load user data for birthday badge calculation
    const { doc, getDoc } = await import('firebase/firestore');
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.exists() ? userDoc.data() : null;
    
    // Load teacher-assigned badges from user document
    const teacherAssignedBadges = userData?.teacherAssignedBadges || [];
    
    // Load previously earned practice badges from user document
    const previouslyEarnedBadges = userData?.earnedBadges || [];

    // Calculate practice-earned badges (cast userData to User type)
    const calculatedPracticeBadges = calculateAllPracticeBadges(sessions, userData as User || undefined);
    
    // Find new badges that haven't been earned before
    const newBadges = calculatedPracticeBadges.filter(badgeId => !previouslyEarnedBadges.includes(badgeId));
    
    // If there are new badges, save them to the database
    if (newBadges.length > 0) {
      try {
        const { updateDoc, arrayUnion } = await import('firebase/firestore');
        await updateDoc(doc(db, 'users', userId), {
          earnedBadges: arrayUnion(...newBadges)
        });
        console.log('New badges saved to database:', newBadges);
      } catch (error) {
        console.error('Error saving new badges to database:', error);
      }
    }
    
    // Combine all badges and remove duplicates
    const combinedBadges = [...calculatedPracticeBadges, ...teacherAssignedBadges];
    const allBadges = Array.from(new Set(combinedBadges));
    
    return allBadges;
  } catch (error) {
    console.error('Error loading user badges:', error);
    return [];
  }
};
